import { Card, Col, Row, Select, Spin, Tag, Upload } from 'antd';
import { Button, Spinner } from '@/ui';
import { CONTENT_PAGE_CREATE_ID } from '@/models/blog-post.model';
import { SupportedLocales } from '@/models/localization.model';
import ContentMetaForm from '../../components/ContentMetaForm/ContentMetaForm';
import BlockPalette from '../../components/BlockPalette/BlockPalette';
import SortableBlockCanvas from '../../components/SortableBlockCanvas/SortableBlockCanvas';
import SchemaBlockInspector from '../../components/SchemaBlockInspector/SchemaBlockInspector';
import { StyledHeaderBlock, StyledPageTitle, StyledPageWrapper } from './ContentPageEditorContainerStyles';
import { BLOG_IMAGE_ACCEPT, resolveBlogMediaDisplayUrl } from '../../helpers/blogMedia';
import { useContentPageEditor } from '../../hooks';

const ContentPageEditorContainer = () => {
  const {
    id,
    t,
    locales,
    activeLocale,
    editor,
    aux,
    selectedBlock,
    selectedSchema,
    inspectorBlock,
    schemaByType,
    blogCategoryOptions,
    previewUrl,
    canPreview,
    previewTitle,
    coverPreviewUrl,
    handleSave,
    handleCancelDraft,
    handlePublishedChange,
    handleEditorBlurCapture,
    handleCoverDrop,
    handleAddBlock,
    handleRemoveBlock,
    handleReorder,
    handleInspectorChange,
    handleMetaChange,
    setSelectedBlockId,
    setLocaleTitle,
    setLocaleDescription,
    setPinned,
    setBlogCategoryId,
  } = useContentPageEditor();

  if (editor.isPageLoading || !aux.schemasReady)
    return (
      <StyledPageWrapper>
        <Spinner />
      </StyledPageWrapper>
    );
  if (!editor.page)
    return (
      <StyledPageWrapper>
        <StyledPageTitle>{t('load_error')}</StyledPageTitle>
      </StyledPageWrapper>
    );

  const inspectorShownBlock = inspectorBlock ?? selectedBlock;

  return (
    <StyledPageWrapper onBlurCapture={handleEditorBlurCapture}>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('editor_title')}</StyledPageTitle>
        <Row gutter={[8, 8]} align="middle" wrap>
          {editor.page.isDraft && id !== CONTENT_PAGE_CREATE_ID ? (
            <>
              <Col>
                <Tag color="red">{t('content_pages_draft_badge', { ns: 'common' })}</Tag>
              </Col>
              <Col>
                <Button
                  onClick={handleCancelDraft}
                  loading={editor.isReverting}
                  disabled={editor.isSaving || editor.isReverting}
                >
                  {t('editor_cancel')}
                </Button>
              </Col>
            </>
          ) : null}
          <Col>
            {canPreview ? (
              <a href={previewUrl} target="_blank" rel="noreferrer">
                <Button>{t('content_pages_list_action_preview', { ns: 'common' })}</Button>
              </a>
            ) : (
              <Button disabled title={previewTitle}>
                {t('content_pages_list_action_preview', { ns: 'common' })}
              </Button>
            )}
          </Col>
          <Col>
            <Button type="primary" onClick={handleSave} loading={editor.isSaving} disabled={editor.isReverting}>
              {t('save')}
            </Button>
          </Col>
        </Row>
      </StyledHeaderBlock>

      <ContentMetaForm
        pageType={editor.page.pageType}
        title={editor.page.title}
        tags={editor.page.tags}
        onChange={handleMetaChange}
        localeTitles={{
          [SupportedLocales.UK]: editor.page.localeMeta?.[SupportedLocales.UK]?.name ?? '',
          [SupportedLocales.RU]: editor.page.localeMeta?.[SupportedLocales.RU]?.name ?? '',
        }}
        onLocaleTitleChange={setLocaleTitle}
        localeDescriptions={{
          [SupportedLocales.UK]: editor.page.localeMeta?.[SupportedLocales.UK]?.description ?? '',
          [SupportedLocales.RU]: editor.page.localeMeta?.[SupportedLocales.RU]?.description ?? '',
        }}
        onLocaleDescriptionChange={setLocaleDescription}
        published={editor.page.published}
        isPinned={editor.page.isPinned}
        onPublishedChange={handlePublishedChange}
        onPinnedChange={setPinned}
        hideTags
        hidePageType
        titleRequired
        invalidNameLocales={editor.metaFieldErrors?.invalidNameLocales ?? []}
      />

      <Card title={t('blog_settings_title')} style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={24}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>{t('field_blog_category')}</div>
            <Select
              allowClear
              placeholder={t('field_blog_category_placeholder')}
              style={{ width: '100%', maxWidth: 480 }}
              options={blogCategoryOptions}
              value={editor.page.blogCategoryId ?? undefined}
              onChange={(v) => setBlogCategoryId(v ?? null)}
            />
          </Col>
          <Col xs={24} md={24}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              {t('field_cover_image')}
              <span style={{ color: '#ff4d4f', marginLeft: 4 }} aria-hidden>
                *
              </span>
            </div>
            <Spin spinning={aux.coverUploading}>
              <Upload.Dragger
                maxCount={1}
                showUploadList={false}
                accept={BLOG_IMAGE_ACCEPT}
                beforeUpload={handleCoverDrop}
                disabled={aux.coverUploading}
                style={{ marginBottom: 12 }}
              >
                <p>Drag and drop cover image here</p>
                <p>or click to select file</p>
              </Upload.Dragger>
            </Spin>
            {coverPreviewUrl || editor.page.coverImage ? (
              <img
                src={
                  coverPreviewUrl ??
                  resolveBlogMediaDisplayUrl(typeof editor.page.coverImage === 'string' ? editor.page.coverImage : '')
                }
                alt="Cover preview"
                style={{ maxWidth: '100%', maxHeight: 200, marginBottom: 12, borderRadius: 8 }}
              />
            ) : null}
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={7}>
          <BlockPalette schemas={aux.schemas} onAdd={handleAddBlock} />
        </Col>
        <Col xs={24} lg={10}>
          <SortableBlockCanvas
            blocks={editor.page.blocks}
            schemaByType={schemaByType}
            locales={locales}
            previewLocale={activeLocale}
            selectedId={editor.selectedBlockId}
            onReorder={handleReorder}
            onSelect={setSelectedBlockId}
            onRemove={handleRemoveBlock}
          />
        </Col>
        <Col xs={24} lg={7}>
          <SchemaBlockInspector
            block={inspectorShownBlock}
            schema={selectedSchema}
            locales={locales}
            onChange={handleInspectorChange}
            blockTypeLabel={inspectorShownBlock ? schemaByType[inspectorShownBlock.type]?.label : undefined}
          />
        </Col>
      </Row>
    </StyledPageWrapper>
  );
};

export default ContentPageEditorContainer;
